import { motion } from 'framer-motion';
import { Check } from '@phosphor-icons/react';
import { OFFER_TIERS, useCart } from '../lib/CartContext';

const RewardMeter = ({ compact = false }) => {
  const { paidTotal, unlockedTierIds, nextTier, amountToNextTier, freeRewards, openPicker } = useCart();

  const maxThreshold = OFFER_TIERS[OFFER_TIERS.length - 1].threshold;
  const progressPct = Math.min(100, Math.round((paidTotal / maxThreshold) * 100));

  const allUnlocked = unlockedTierIds.length === OFFER_TIERS.length;

  return (
    <div
      className={`w-full ${compact ? 'space-y-2' : 'space-y-3'} rounded-sm border border-border/40 bg-secondary/60 p-3`}
      data-testid="reward-meter"
    >
      <div className="flex items-center justify-between text-xs">
        <span className="font-heading font-bold text-foreground tracking-wide">
          UNLOCK REWARDS
        </span>
        <span className="text-muted-foreground">
          {allUnlocked
            ? 'All rewards unlocked! 🎉'
            : nextTier
              ? (<>Add <span className="text-foreground font-bold">₹{amountToNextTier}</span> more for <span className="text-foreground font-bold">{nextTier.label}</span></>)
              : ''}
        </span>
      </div>

      <div className="relative h-2 w-full bg-background rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-white via-white to-white/90 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progressPct}%` }}
          transition={{ type: 'spring', damping: 26, stiffness: 200 }}
        />
        {OFFER_TIERS.map((tier) => {
          const pos = Math.min(100, (tier.threshold / maxThreshold) * 100);
          const unlocked = unlockedTierIds.includes(tier.id);
          return (
            <div
              key={tier.id}
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
              style={{ left: `${pos}%` }}
            >
              <div
                className={`w-3 h-3 rounded-full border-2 transition-all ${
                  unlocked
                    ? 'bg-white border-white scale-125 shadow-[0_0_10px_rgba(255,255,255,0.6)]'
                    : 'bg-background border-muted-foreground/60'
                }`}
              />
            </div>
          );
        })}
      </div>

      <ul className="grid grid-cols-3 gap-2 pt-1">
        {OFFER_TIERS.map((tier) => {
          const unlocked = unlockedTierIds.includes(tier.id);
          const picked = freeRewards[tier.id];
          const needsPicker = tier.needsPicker && unlocked && !picked;
          return (
            <li
              key={tier.id}
              className={`text-center transition-opacity ${unlocked ? 'opacity-100' : 'opacity-45'}`}
              data-testid={`tier-${tier.id}`}
            >
              <motion.div
                animate={unlocked ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-lg leading-none mb-1"
                aria-hidden
              >
                {tier.icon}
              </motion.div>
              <p className="text-[10px] font-bold uppercase tracking-wide leading-tight text-foreground">
                {tier.label}
              </p>
              <p className="text-[10px] text-muted-foreground">₹{tier.threshold}+</p>
              {needsPicker && (
                <button
                  type="button"
                  onClick={() => openPicker(tier.id)}
                  className="mt-1 text-[10px] font-bold text-white bg-black border border-white/60 px-2 py-1 rounded-sm hover:bg-white hover:text-black transition-colors inline-flex items-center gap-1"
                  data-testid={`pick-${tier.id}`}
                >
                  Choose
                </button>
              )}
              {unlocked && picked && (
                <p className="mt-1 text-[10px] text-green-400 font-bold inline-flex items-center gap-0.5 justify-center">
                  <Check size={10} weight="bold" /> Picked
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default RewardMeter;
